import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import PromotionDetailListing from '@/modules/dashboard/components/PromotionAnalytics/components/PromotionDetailListing'
import useQueryGetPromotionDetail from '@/modules/dashboard/api/hooks/useQueryGetPromotionDetail'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { useState, useEffect } from 'react'
import { format, startOfDay, endOfDay } from 'date-fns'
import { useQueryState, parseAsString } from 'nuqs'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { useParams } from 'react-router-dom'

type SortOrder = 'asc' | 'desc' | undefined

function PromotionDetailPage() {
	const { promotionId } = useParams<{ promotionId: string }>()
	const { pageSize, currentPage, searchQuery } = useTablePagination()
	const { dateFilter, setDateFilter } = useDateFilter()
	const [dateRange, setDateRange] = useQueryState(
		'dateRange',
		parseAsString.withDefault(''),
	)

	useEffect(() => {
		// Only set today's date if there are no dates AND dateRange is not 'all'
		if (!dateFilter.startDate && !dateFilter.endDate && dateRange !== 'all') {
			const today = new Date()
			setDateFilter({
				startDate: format(startOfDay(today), 'yyyy-MM-dd'),
				endDate: format(endOfDay(today), 'yyyy-MM-dd'),
			})
			setDateRange('today')
		}
	}, [
		dateFilter.startDate,
		dateFilter.endDate,
		dateRange,
		setDateFilter,
		setDateRange,
	])

	const [totalReceiveTimeSort, setTotalReceiveTimeSort] = useState<SortOrder>()
	const [totalDepositAmountSort, setTotalDepositAmountSort] =
		useState<SortOrder>()
	const [totalTransferAmountSort, setTotalTransferAmountSort] =
		useState<SortOrder>()
	const [balanceAfterTurnoverFinishSort, setBalanceAfterTurnoverFinishSort] =
		useState<SortOrder>()
	const [
		playerTurnoverAmountAfterClearTurnoverSort,
		setPlayerTurnoverAmountAfterClearTurnoverSort,
	] = useState<SortOrder>()

	const handleSort = (columnId: string, order: SortOrder) => {
		// Reset all sorts first
		setTotalReceiveTimeSort(undefined)
		setTotalDepositAmountSort(undefined)
		setTotalTransferAmountSort(undefined)
		setBalanceAfterTurnoverFinishSort(undefined)
		setPlayerTurnoverAmountAfterClearTurnoverSort(undefined)

		// Set the specific sort
		if (columnId === 'totalReceiveTime') {
			setTotalReceiveTimeSort(order)
		} else if (columnId === 'totalDepositAmount') {
			setTotalDepositAmountSort(order)
		} else if (columnId === 'totalTransferAmount') {
			setTotalTransferAmountSort(order)
		} else if (columnId === 'balanceAfterTurnoverFinish') {
			setBalanceAfterTurnoverFinishSort(order)
		} else if (columnId === 'playerTurnoverAmountAfterClearTurnover') {
			setPlayerTurnoverAmountAfterClearTurnoverSort(order)
		}
	}

	const payload = {
		promotionId: promotionId!,
		take: pageSize,
		skip: (currentPage - 1) * pageSize,
		phone: searchQuery || undefined,
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
		totalReceiveTimeSort,
		totalDepositAmountSort,
		totalTransferAmountSort,
		balanceAfterTurnoverFinishSort,
		playerTurnoverAmountAfterClearTurnoverSort,
	}

	const { data, isLoading } = useQueryGetPromotionDetail(payload, {
		enabled: !!promotionId,
	} as any)

	if (!promotionId) {
		return
	}

	return (
		<PageContainer>
			<PromotionDetailListing
				data={data?.data || []}
				total={data?.total || 0}
				isLoading={isLoading}
				onSort={handleSort}
				promotionName={data?.data[0]?.promotionName}
			/>
		</PageContainer>
	)
}

const GuardedPromotionDetailPage = withUserPermission(PromotionDetailPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_MARKETING],
})

export default GuardedPromotionDetailPage
